import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Card, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useProcesses, type ProcessStatus, type ProcessType } from '@/api/capacities';
import { paginationView } from '@/pages/customers/pagination-summary';
import { processColumns } from './list-configs/process-column-definitions';
import { ProcessListToolbar } from './process-list-toolbar';

const PAGE_SIZE = 20;

/** Debounce a fast-changing value (search box) to throttle server refetches. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Warehouse Processes list — server-paginated `DataTable` (code · type · status ·
 * route · date) under a toolbar with search + type/status filters. Rows link to
 * the process detail. Read-only (W4 scope); create lives at `/capacities/processes/new`.
 */
export default function CapacityProcessesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [type, setType] = useState<ProcessType>();
  const [status, setStatus] = useState<ProcessStatus>();

  const params = {
    page,
    page_size: PAGE_SIZE,
    search: query || undefined,
    type: type || undefined,
    status: status || undefined,
  };
  const { data, isLoading, isError, isFetching, refetch } = useProcesses(params);

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const resetPage = () => setPage(1);

  const footer = useMemo(() => {
    const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
    return (
      <Pagination
        page={page}
        pageCount={view.pageCount}
        onPageChange={setPage}
        summary={`${view.summary}${isFetching ? ' …' : ''}`}
      />
    );
  }, [page, rows.length, total, pageCount, isFetching]);

  return (
    <Container size="full">
      <PageHeader
        title="Processes"
        description="Warehouse import, export, transfer and inventory processes with two-level approval."
        actions={
          <Button variant="primary" onClick={() => navigate('/capacities/processes/new')}>
            New process
          </Button>
        }
        style={{ marginBottom: 'var(--vn-space-4)' }}
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <ProcessListToolbar
          query={rawQuery}
          onQueryChange={(v) => {
            setRawQuery(v);
            resetPage();
          }}
          type={type}
          onTypeChange={(v) => {
            setType(v);
            resetPage();
          }}
          status={status}
          onStatusChange={(v) => {
            setStatus(v);
            resetPage();
          }}
        />

        {isError ? (
          <Alert
            tone="danger"
            title="Failed to load processes"
            actions={
              <Button size="sm" variant="ghost" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : isLoading ? (
          <Card flat>
            <div style={{ padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 24 }} />
              ))}
            </div>
          </Card>
        ) : (
          <DataTable
            columns={processColumns}
            data={rows}
            getRowId={(r) => r.id}
            onRowClick={(r) => navigate(`/capacities/processes/${r.id}`)}
            emptyState={
              <EmptyState
                icon={<ClipboardList size={24} />}
                title="No processes found"
                description="Try adjusting your search or filters."
              />
            }
            footer={footer}
          />
        )}
      </div>
    </Container>
  );
}
